import { createContext, useContext, useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { CfLoader } from "@/shared/components/common/CfLoader/CfLoader";
import { logger } from "@/shared/services/SentryLogger.service";

import {
  ALL_FARM_MODULES_ENABLED,
  type FarmModule,
  type FarmModuleSettings,
} from "./farmModules.constants";

import { farmModulesQuery } from "./farmModules.api";

type FarmModules = {
  settings: FarmModuleSettings;
  isEnabled: (module: FarmModule) => boolean;
  isDisabled: (module: FarmModule) => boolean;
};

const FarmModulesContext = createContext<FarmModuleSettings>(
  ALL_FARM_MODULES_ENABLED,
);

type FarmModulesProviderProps = {
  farmId: string;
  children: React.ReactNode;
};

const FarmModulesProvider = ({
  children,
  farmId,
}: FarmModulesProviderProps): React.ReactElement => {
  const { data, error, isFetched } = useQuery(farmModulesQuery(farmId));

  useEffect(() => {
    if (!error) {
      return;
    }

    logger.warn("Farm modules unavailable, showing all navigation", { farmId });
  }, [error, farmId]);

  // Not `isPending`: a query that failed has no data, so every refetch (each mutation invalidates all
  // queries) puts it back to pending - the loader would unmount the whole farm and lose its state.
  if (!isFetched) {
    return <CfLoader />;
  }

  return (
    <FarmModulesContext.Provider value={data ?? ALL_FARM_MODULES_ENABLED}>
      {children}
    </FarmModulesContext.Provider>
  );
};

const useFarmModules = (): FarmModules => {
  const settings = useContext(FarmModulesContext);

  return useMemo(
    () => ({
      settings,
      isEnabled: (module: FarmModule) => settings[module],
      isDisabled: (module: FarmModule) => !settings[module],
    }),
    [settings],
  );
};

export { FarmModulesProvider, useFarmModules };
export type { FarmModules };
